import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { verwendungOptionsFor, type VerwendungIconKey } from "./verwendungen";

/**
 * Visual tile-picker for "Verwendung" (vehicle usage), conditional on the
 * vehicle class selected in the previous step.
 *
 * Welche Optionen es je Fahrzeugklasse gibt — und ob es überhaupt welche gibt
 * — steht in `verwendungen.ts`; das Schema gatet die Frage mit derselben
 * Liste, damit der Fortschritt keine Verwendung zählt, die hier gar nicht
 * auswählbar ist.
 */

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "8px",
    paddingTop: "18px",
    paddingBottom: "16px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "140px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "60px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.ink,
    lineHeight: 1.2,
  },
  desc: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    lineHeight: 1.3,
  },
  notice: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
});

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="60"
    height="44"
    viewBox="0 0 60 44"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons: Record<VerwendungIconKey, () => JSX.Element> = {
  eigen: () => (
    <Svg>
      <rect x="6" y="14" width="36" height="20" rx="3" />
      <circle cx="14" cy="34" r="3" />
      <circle cx="36" cy="34" r="3" />
      <path d="M44 22h10v10h-10z" />
      <path d="M49 22v-6" />
      <path d="M46 18h6" />
    </Svg>
  ),
  kurier: () => (
    <Svg>
      <path d="M4 20h26v14H4z" />
      <path d="M30 24h12l6 6v4H30z" />
      <circle cx="12" cy="36" r="3" />
      <circle cx="40" cy="36" r="3" />
      <rect x="12" y="6" width="10" height="10" rx="1" />
      <path d="M14 9h6M14 13h4" />
    </Svg>
  ),
  werkverkehr: () => (
    <Svg>
      <path d="M4 12h24v20H4z" />
      <path d="M28 18h12l8 8v6H28z" />
      <circle cx="12" cy="34" r="3" />
      <circle cx="40" cy="34" r="3" />
      <path d="M30 8h18v4H30z" />
    </Svg>
  ),
  gueter: () => (
    <Svg>
      <path d="M4 12h26v20H4z" />
      <path d="M30 18h12l8 8v6H30z" />
      <circle cx="12" cy="34" r="3" />
      <circle cx="40" cy="34" r="3" />
      <rect x="8" y="16" width="16" height="12" />
      <path d="M16 16v12M8 22h16" />
    </Svg>
  ),
  land: () => (
    <Svg>
      <circle cx="16" cy="32" r="8" />
      <circle cx="16" cy="32" r="3" />
      <circle cx="44" cy="34" r="5" />
      <path d="M10 22V12h10l4 6h4v10h-4" />
      <path d="M30 8c2 0 4 1 4 4M36 6c1 2 1 4-1 6" />
    </Svg>
  ),
  wohnwagen: () => (
    <Svg>
      <path d="M4 14h44v18H4z" />
      <path d="M48 24h10" />
      <circle cx="16" cy="34" r="3" />
      <circle cx="36" cy="34" r="3" />
      <rect x="10" y="18" width="14" height="10" />
      <path d="M28 18h12v10H28z" />
    </Svg>
  ),
};

type Props = {
  vehicleClass: string;
  value: string;
  onChange: (value: string) => void;
};

const VerwendungPicker = ({ vehicleClass, value, onChange }: Props) => {
  const styles = useStyles();
  const options = verwendungOptionsFor(vehicleClass);

  if (!vehicleClass) {
    return (
      <div className={styles.notice}>
        Bitte wählen Sie zuerst die Fahrzeugart aus — die Verwendungsoptionen hängen davon ab.
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className={styles.notice}>
        Für diese Fahrzeugart sind keine speziellen Verwendungsoptionen hinterlegt. Sie können
        direkt zum nächsten Schritt weitergehen.
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {options.map((o) => {
        const Render = Icons[o.iconKey];
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(active ? "" : o.value)}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            aria-pressed={active}
          >
            <span className={styles.iconWrap}>
              <Render />
            </span>
            <span className={styles.label}>{o.label}</span>
            {o.desc && <span className={styles.desc}>{o.desc}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default VerwendungPicker;
